import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Fixture } from './sportsApi';
import type {
  CatalogueResponse,
  Market,
  Runner,
  FancyMarket,
  FancyMarketItem,
} from '@/types/MatchData';

/**
 * Diamond Sports API — now proxied through the app backend (`/api/diamond/*`) so the
 * admin can hide / pin / suspend matches (via a DB overlay) and the API key stays
 * server-side. The backend applies overrides before returning data to users.
 */

// Event Type IDs used by Diamond (Betfair convention).
export const DIAMOND_ETID = {
  cricket: 4,
  soccer: 1,
  tennis: 2,
} as const;

// Home-tab / route sport name → Diamond etid (only these sports use Diamond).
export const SPORT_TO_ETID: Record<string, number> = {
  cricket: 4,
  soccer: 1,
  football: 1,
  tennis: 2,
};
const etidToSport = (etid: number): string =>
  etid === 1 ? 'Soccer' : etid === 2 ? 'Tennis' : 'Cricket';

/* ───────────────── Raw Diamond response shapes ───────────────── */
interface DiamondOdd {
  odds: number;
  oname: string;
  otype: 'back' | 'lay';
  sid: number;
  size: number;
}
interface DiamondSection {
  nat: string;
  sno: number;
  sid: number;
  gstatus: string;
  odds: DiamondOdd[];
}
interface DiamondMatch {
  gmid: number;
  cid: number;
  cname: string;
  ename: string;
  etid: number;
  stime: string;
  iplay: boolean;
  bm: boolean;
  f: boolean;
  f1: boolean;
  tv: boolean;
  status: string;
  section?: DiamondSection[];
}
interface DiamondHighlightResponse {
  data?: { data?: { t1?: DiamondMatch[] } };
}

/* ───────────────── Mapper: Diamond match → app Fixture ─────────────────
 * BettingTable reads runners[0]=col "1", runners[1]=col "2", runners[2]=col "X".
 * Diamond `section` holds the two teams (by sno) plus an optional "The Draw".
 */
// The feed marks a tradable runner ACTIVE (cricket) or OPEN (soccer / tennis);
// anything else (SUSPENDED, CLOSED, …) hides the prices.
const TRADABLE = new Set(['ACTIVE', 'OPEN']);

const emptyRunner = (s?: DiamondSection, fallbackName = '') => {
  const back = s?.odds?.find((o) => o.otype === 'back')?.odds;
  const lay = s?.odds?.find((o) => o.otype === 'lay')?.odds;
  const suspended = s?.gstatus && !TRADABLE.has(s.gstatus.toUpperCase());
  return {
    handicap: 0,
    runnerName: s?.nat?.trim() || fallbackName,
    selectionId: String(s?.sid ?? ''),
    sortPriority: String(s?.sno ?? ''),
    backPrice1: suspended ? undefined : back,
    backPrice2: undefined,
    backPrice3: undefined,
    layPrice1: suspended ? undefined : lay,
    layPrice2: undefined,
    layPrice3: undefined,
    backSize1: 0,
    backSize2: 0,
    backSize3: 0,
    laySize1: 0,
    laySize2: 0,
    laySize3: 0,
  };
};

const mapMatchToFixture = (m: DiamondMatch): Fixture => {
  const sections = m.section ?? [];
  const draw = sections.find((s) => /draw/i.test(s.nat));
  const teams = sections.filter((s) => s !== draw).sort((a, b) => a.sno - b.sno);

  // Matches without a market yet come with an empty `section`, so take the
  // sides from the event name ("A v B" / "A - B") rather than showing blanks.
  const [home = '', away = ''] = (m.ename ?? '').split(/\s+(?:v|vs|-)\s+/i).map((s) => s.trim());
  const runners = [emptyRunner(teams[0], home), emptyRunner(teams[1], away), emptyRunner(draw)];

  return {
    id: m.gmid,
    competitionId: m.cid,
    providerId: null,
    externalId: String(m.gmid),
    name: m.ename,
    sport: etidToSport(m.etid),
    startTime: m.stime,
    status: m.status,
    isFancy: !!(m.f || m.f1),
    isBookmaker: !!m.bm,
    isPremiumFancy: false,
    isPopular: true,
    inplay: !!m.iplay,
    competition: { id: m.cid, externalId: String(m.cid), name: m.cname },
    lay: [],
    back: [],
    runners,
  } as unknown as Fixture;
};

/* ───────────────── Game-data (match detail) shapes + mapper ─────────────────
 * gamedataPrivate returns data.data = an array of markets. Market 0 is MATCH_ODDS
 * (gtype "match"); the rest are session/fancy markets (fancy, fancy1, khado,
 * oddeven, meter). Each market has section[] (runners); each section has odds[]
 * with oname back1/back2/back3 + lay1/lay2/lay3.
 */
interface DiamondMarketSectionOdd {
  odds: number;
  oname: string;
  otype: 'back' | 'lay';
  size: number;
}
interface DiamondMarketSection {
  nat: string;
  sno: number;
  sid: number;
  gstatus: string;
  odds?: DiamondMarketSectionOdd[];
}
interface DiamondMarket {
  mid: number;
  mname: string;
  gtype: string;
  status: string;
  iplay: boolean;
  min: number;
  max: number;
  section?: DiamondMarketSection[];
}
interface DiamondGameDataResponse {
  data?: { data?: DiamondMarket[] };
}

const oddByName = (s: DiamondMarketSection, name: string) =>
  s.odds?.find((o) => o.oname === name);

const isSuspended = (gstatus?: string) =>
  !!gstatus && !TRADABLE.has(gstatus.toUpperCase());

// MATCH_ODDS section → full Runner (3 back + 3 lay levels).
const mapMatchOddsRunner = (s: DiamondMarketSection): Runner => {
  const b1 = oddByName(s, 'back1');
  const b2 = oddByName(s, 'back2');
  const b3 = oddByName(s, 'back3');
  const l1 = oddByName(s, 'lay1');
  const l2 = oddByName(s, 'lay2');
  const l3 = oddByName(s, 'lay3');
  const back = [b1, b2, b3].filter(Boolean).map((o) => ({ price: o!.odds, size: o!.size }));
  const lay = [l1, l2, l3].filter(Boolean).map((o) => ({ price: o!.odds, size: o!.size }));
  return {
    handicap: 0,
    selectionId: String(s.sid),
    sortPriority: s.sno,
    runnerName: s.nat,
    status: isSuspended(s.gstatus) ? 'Suspended' : 'Active',
    back,
    lay,
    backPrice1: b1?.odds,
    backSize1: b1?.size,
    backPrice2: b2?.odds,
    backSize2: b2?.size,
    backPrice3: b3?.odds,
    backSize3: b3?.size,
    layPrice1: l1?.odds,
    laySize1: l1?.size,
    layPrice2: l2?.odds,
    laySize2: l2?.size,
    layPrice3: l3?.odds,
    laySize3: l3?.size,
  };
};

// Session/fancy section → FancyMarketItem (single back1 + lay1).
/**
 * Diamond's market kind → the vocabulary the BET ENGINE uses.
 *
 * This is money-critical, not cosmetic. `marketName` decides which exposure
 * formula runs (exposure.service only treats 'session' as a run-line market;
 * everything else is priced as back/lay) and which bets the settlement query
 * picks up (it matches market_name IN 'session','fancy','oddeven'). Diamond's
 * own labels — "8 over run AFGV" — match none of those, so sending the label
 * here books the wrong exposure and the bet never settles.
 *
 * The label is NOT lost: it travels as runnerName, which the backend stores in
 * bet.selection and which settlement joins to the result feed's marketName
 * (verified against a live match: '8 over run AFGV' ↔ '8 OVER RUN AFGV').
 *
 * Only the kinds seen on the live feed are mapped. Anything else falls through
 * to '' so it can be spotted and mapped deliberately rather than settling on a
 * guessed formula.
 */
const BET_MARKET_NAME: Record<string, string> = {
  fancy: 'session', // run-line: "8 over run", "fall of 3rd wkt" — percentage-based
  fancy1: 'session',
  oddeven: 'oddeven',
};

const betMarketName = (gtype: string) => BET_MARKET_NAME[(gtype ?? '').toLowerCase()] ?? '';

const mapFancyItem = (s: DiamondMarketSection, category: string, gtype: string): FancyMarketItem => {
  const b1 = oddByName(s, 'back1');
  const l1 = oddByName(s, 'lay1');
  return {
    marketId: String(s.sid ?? s.sno),
    // The engine's category, not the display label — see BET_MARKET_NAME above.
    marketName: betMarketName(gtype),
    marketCategory: category,
    gameType: gtype,
    gameStatus: s.gstatus ?? '',
    ballSession: '',
    sortPriority: s.sno ?? 0,
    runners: {
      selectionId: String(s.sid ?? ''),
      runnerName: s.nat,
      status: (s.gstatus ?? '').toUpperCase() === 'SUSPENDED' ? 'Suspended' : 'Active',
      back: b1 ? [{ price: b1.odds, size: b1.size }] : [],
      lay: l1 ? [{ price: l1.odds, size: l1.size }] : [],
      backPrice1: b1?.odds,
      backSize1: b1?.size,
      layPrice1: l1?.odds,
      laySize1: l1?.size,
    },
  };
};

// Prettier category labels for the fancy section headers.
const FANCY_LABELS: Record<string, string> = {
  Normal: 'Fancy',
  fancy1: 'Fancy1',
  khado: 'Khado',
  oddeven: 'Odd/Even',
  meter: 'Meter',
};

const mapGameDataToCatalogue = (
  res: DiamondGameDataResponse,
  gmid: string,
  sport = 'Cricket'
): CatalogueResponse => {
  const marketsArr = res?.data?.data ?? [];
  const matchOdds = marketsArr.find((m) => m.gtype === 'match' || m.mname === 'MATCH_ODDS');

  const markets: Record<string, Market[]> = {};
  if (matchOdds) {
    const teamSections = (matchOdds.section ?? []).filter((s) => !/draw/i.test(s.nat));
    const draw = (matchOdds.section ?? []).find((s) => /draw/i.test(s.nat));
    const ordered = [...teamSections, ...(draw ? [draw] : [])];
    markets['Match Odds'] = [
      {
        marketId: String(matchOdds.mid),
        marketName: 'Match Odds',
        eventId: gmid,
        inplay: !!matchOdds.iplay,
        marketStartTime: '',
        status: matchOdds.status === 'OPEN' ? 'Active' : 'Suspended',
        marketType: 'Match Odds',
        runners: ordered.map(mapMatchOddsRunner),
        maxBetAmount: matchOdds.max || null,
        minBetAmount: matchOdds.min || null,
        minRate: null,
        maxRate: null,
      },
    ];
  }

  const fancyMarkets: FancyMarket = {};
  marketsArr
    .filter((m) => m !== matchOdds && (m.section?.length ?? 0) > 0)
    .forEach((m) => {
      const label = FANCY_LABELS[m.mname] ?? FANCY_LABELS[m.gtype] ?? m.mname ?? m.gtype;
      const items = (m.section ?? []).map((s) => mapFancyItem(s, label, m.gtype));
      fancyMarkets[label] = [...(fancyMarkets[label] ?? []), ...items];
    });

  const teamNames = (matchOdds?.section ?? [])
    .filter((s) => !/draw/i.test(s.nat))
    .map((s) => s.nat);

  return {
    success: true,
    message: '',
    catalogue: {
      id: gmid,
      externalId: gmid,
      eventName: teamNames.join(' v '),
      competitionId: 0,
      startTime: '',
      status: 'Active',
      sport,
      inplay: !!matchOdds?.iplay,
      markets,
      premiumMarket: {},
      fancyMarkets,
    },
    betConfig: {
      minBetAmount: '100',
      maxBetAmount: '500000',
      sessionMinBetAmount: '100',
      sessionMaxBetAmount: '25000',
      minRate: null,
      maxRate: null,
      sessionMinRate: null,
      sessionMaxRate: null,
    },
  };
};

/* Game meta (event name + start time) from gamedetailPrivate. */
interface DiamondGameMetaResponse {
  data?: { data?: { ename: string; stime: string; iplay: boolean }[] };
}
/* Scorecard iframe URL. */
interface DiamondScorecardResponse {
  scorecard_url?: string;
}

export const diamondApi = createApi({
  reducerPath: 'diamondApi',
  // Now proxied through the app backend (so admin can hide/pin/suspend matches and
  // the API key stays server-side). Dev + prod: VITE_API_BASE_URL backend.
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/diamond`,
  }),
  endpoints: (builder) => ({
    // Cricket highlights for the home betting table.
    getDiamondHighlights: builder.query<Fixture[], { etid?: number }>({
      query: ({ etid = DIAMOND_ETID.cricket } = {}) => ({
        url: '/highlights',
        params: { etid },
      }),
      transformResponse: (res: DiamondHighlightResponse) =>
        (res?.data?.data?.t1 ?? [])
          .map(mapMatchToFixture)
          // Match the official ARG Diamond ordering: live matches first, then by start time.
          .sort((a, b) => {
            if (a.inplay !== b.inplay) return a.inplay ? -1 : 1;
            return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          }),
    }),

    // Full match detail (odds + fancy markets) mapped to the app's CatalogueResponse.
    getDiamondGameData: builder.query<CatalogueResponse, { gmid: string; etid?: number }>({
      query: ({ gmid, etid = DIAMOND_ETID.cricket }) => ({
        url: '/gamedata',
        params: { gmid, etid },
      }),
      transformResponse: (res: DiamondGameDataResponse, _meta, arg) =>
        mapGameDataToCatalogue(res, arg.gmid, etidToSport(arg.etid ?? 4)),
    }),

    // Event name + start time for the detail header.
    getDiamondGameMeta: builder.query<
      { eventName: string; startTime: string; inplay: boolean },
      { gmid: string; etid?: number }
    >({
      query: ({ gmid, etid = DIAMOND_ETID.cricket }) => ({
        url: '/gamedetail',
        params: { gmid, etid },
      }),
      transformResponse: (res: DiamondGameMetaResponse) => {
        const d = res?.data?.data?.[0];
        return { eventName: d?.ename ?? '', startTime: d?.stime ?? '', inplay: !!d?.iplay };
      },
    }),

    // Live scorecard iframe URL.
    getDiamondScorecard: builder.query<{ scorecardUrl?: string }, { gmid: string; etid?: number }>({
      query: ({ gmid, etid = DIAMOND_ETID.cricket }) => ({
        url: '/scorecard',
        params: { gmid, etid },
      }),
      transformResponse: (res: DiamondScorecardResponse) => ({ scorecardUrl: res?.scorecard_url }),
    }),
  }),
});

export const {
  useGetDiamondHighlightsQuery,
  useGetDiamondGameDataQuery,
  useGetDiamondGameMetaQuery,
  useGetDiamondScorecardQuery,
} = diamondApi;
